/**
 * Interactive Survey Market Map Component
 *
 * This component provides an interactive map interface for viewing survey properties
 * using Mapbox GL JS for high-performance vector maps.
 *
 * MAPBOX CONFIGURATION:
 * - Requires a valid Mapbox access token in VITE_MAPBOX_TOKEN environment variable
 * - For development, a fallback public token is used if the environment token is invalid
 */

import { useAppSelector } from "@/app/hooks";
import { LeaseComp } from "@/features/leaseComps/types";
import { POI } from "@/features/poi/types";
import { Property } from "@/features/survey/types";
import { useGetEmeraldBingTilesQuery } from "@/services/bingApi";
import { useUploadMarketMapFileMutation } from "@/services/mediaService";
import { useGetPolygonsQuery } from "@/services/polygonService";
import { useMarketMapLegendPages } from "@/shared/hooks/useMarketMapLegendPages";
import { getPropertyMarkerContent } from "@/shared/utils";
import { MAP_STYLES, MapStyle } from "@/shared/utils/constants";
import {
  createLeaseCompMarkerElement,
  createPoiMarkerElement,
  createPropertyMarkerSvgElement,
  filterLeaseComps,
  filterPois,
  filterPropertiesForMap,
  getIconData,
  getLeaseCompCoordinatesWithMapData,
  getLeaseCompIconData,
  getPoiCoordinates,
  getPoiIconData,
  isEmptyObjectFromStr,
  makeMapKeyCompositeKey,
  marketMapGeneration,
} from "@/shared/utils/map";
import {
  addMarinePortLayer,
  centerMapWithMapData as centerMapUtil,
  deleteMarkers,
  getPDFCoordinates,
} from "@/shared/utils/mapUtils";
import {
  addPolygonsToMap,
  removeAllPolygons,
} from "@/shared/utils/polygonUtils";
import { addRadiusCircle as addRadiusCircleUtil } from "@/shared/utils/radiusCircles";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams, useSearchParams } from "react-router";
import SurveyPDFPage from "./SurveyPDFPage";
import MapKey from "./components/MapKey";
import Footer from "./components/pdf/Footer";
import MarketMapLegends from "./components/pdf/MarketMapLegends";
import PDFHeader from "./components/pdf/PDFHeader";

// Constants
const DEFAULT_MAP_CENTER: [number, number] = [-97, 33];
const DEFAULT_MAP_ZOOM = 4;
const MARKET_MAP_GENERATION_DELAY = 5000;

// Configure Mapbox access token
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

// Validate and set the access token
if (MAPBOX_TOKEN && MAPBOX_TOKEN.startsWith("pk.")) {
  mapboxgl.accessToken = MAPBOX_TOKEN;
} else {
  console.warn(
    "Invalid or missing VITE_MAPBOX_TOKEN, using fallback token for demo purposes",
  );
}

// PDF Paper Size enum - following TypeScript conventions
enum PDFPaperSize {
  Portrait = "Portrait",
  Landscape = "Landscape",
}

// --- Portrait page layout component ---
// Measures the actual rendered height of MarketMapLegends and sizes the map
// container to fill the remaining space, so the layout is always pixel-accurate.
interface PortraitMapPageProps {
  pageIndex: number;
  mapContainerRef?: RefObject<HTMLDivElement | null>;
  showMapKey: boolean;
  showLegend: boolean;
  hasPageContent: boolean;
  pageProperties: Property[];
  startIndexFrom: number;
  pageLeaseComps: LeaseComp[];
  startLeaseIndexFrom: number;
}

const PortraitMapPage = ({
  pageIndex,
  mapContainerRef,
  showMapKey,
  showLegend,
  hasPageContent,
  pageProperties,
  startIndexFrom,
  pageLeaseComps,
  startLeaseIndexFrom,
}: PortraitMapPageProps) => {
  const legendRef = useRef<HTMLDivElement>(null);
  const [legendHeight, setLegendHeight] = useState(0);

  useEffect(() => {
    if (!legendRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setLegendHeight(entry.contentRect.height);
    });
    observer.observe(legendRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="h-full flex flex-col">
      {/* Map container: fills all space above the legend */}
      <div
        className="relative"
        style={{
          flex: legendHeight > 0 ? "none" : 1,
          height:
            legendHeight > 0 ? `calc(100% - ${legendHeight}px)` : undefined,
        }}
      >
        <div
          className="market-map-container w-full h-full"
          ref={mapContainerRef}
          id={`marketmap-${pageIndex}`}
        >
          {showMapKey && <MapKey />}
        </div>
      </div>

      {/* Legend: renders at natural height; ResizeObserver feeds that back above */}
      {showLegend && hasPageContent && (
        <div ref={legendRef} className="flex-shrink-0">
          <MarketMapLegends
            mode="PDF"
            pageProperties={pageProperties}
            startIndexFrom={startIndexFrom}
            pageLeaseComps={pageLeaseComps}
            startLeaseIndexFrom={startLeaseIndexFrom}
          />
        </div>
      )}
    </div>
  );
};

const InteractiveSurveyMarketMapRendered = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const [searchParams] = useSearchParams();
  const isClientShortListParam =
    searchParams.get("isClientShortList") === "true";

  let { currentSurvey, mapFilters, hiddenMapKeyColors } = useAppSelector(
    (state) => state.interactiveSurvey,
  );
  const { marinePorts } = useAppSelector((state) => state.media);
  const isIncludeLeaseComps = currentSurvey?.isIncludeLeaseComps ?? false;
  const isIncludePolygons = currentSurvey?.isIncludePolygons ?? true;
  const { leaseComps: rawLeaseComps } = useAppSelector(
    (state) => state.leaseComps,
  );
  const leaseComps = isIncludeLeaseComps ? rawLeaseComps : [];
  const { pois: rawPois } = useAppSelector((state) => state.poi);
  const pois = rawPois;

  // Use the correct RTK Query hook for uploading market map files
  const [uploadMarketMapFile] = useUploadMarketMapFileMutation();
  const { data: bingTilesData } = useGetEmeraldBingTilesQuery();
  const {
    data: polygons = [],
    isError: isPolygonError,
    isFetching: isPolygonFetching,
  } = useGetPolygonsQuery(surveyId || "", {
    skip: !surveyId || !isIncludePolygons,
  });
  const { getPageData } = useMarketMapLegendPages(
    currentSurvey,
    currentSurvey?.properties || [],
    leaseComps,
  );

  // Map and UI refs
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const leaseCompMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const poiMarkersRef = useRef<mapboxgl.Marker[]>([]);
  const radiusCirclesRef = useRef<Set<string>>(new Set()); // Track property IDs with radius circles
  const radiusLabelMarkersRef = useRef<Map<string, mapboxgl.Marker>>(new Map()); // Track radius label markers
  const polygonIdsRef = useRef<string[]>([]);
  const polygonsRef = useRef(polygons);

  // Update polygonsRef when polygon data changes
  useEffect(() => {
    polygonsRef.current = polygons;
  }, [polygons]);
  const marketMapData =
    currentSurvey?.marketMapBamData &&
    typeof JSON.parse(currentSurvey.marketMapBamData) === "object" &&
    !isEmptyObjectFromStr(currentSurvey.marketMapBamData)
      ? JSON.parse(currentSurvey.marketMapBamData)
      : null;

  // Map and UI state - removed unused sidebar state
  const [currentMapStyle, setCurrentMapStyle] = useState<MapStyle>(() => {
    // Use survey's marketMapStyle if available, otherwise default to first style
    if (currentSurvey?.marketMapStyle) {
      let currentStyle = currentSurvey.marketMapStyle;
      if (currentSurvey.marketMapStyle === "bingHybrid") {
        currentStyle = "emeraldBingHybrid";
      }
      if (currentSurvey.marketMapStyle === "esriHybrid") {
        currentStyle = "emeraldEsriHybrid";
      }
      const surveyStyle = MAP_STYLES.find((style) => style.id === currentStyle);
      if (surveyStyle) {
        if (surveyStyle.id === "emeraldBingHybrid") {
          surveyStyle.url = bingTilesData || "";
        }
        return surveyStyle;
      }
    }
    return MAP_STYLES[2];
  });

  // Use Redux state for filters with query parameter override
  const filters = useMemo(() => {
    // If isClientShortList query param is present, override the showShortlistOnly filter
    if (isClientShortListParam) {
      return {
        ...mapFilters,
        showShortlistOnly: true,
      };
    }
    return mapFilters;
  }, [mapFilters, isClientShortListParam]);

  // Properties data from Redux state
  let properties = useMemo(() => {
    return currentSurvey?.properties || [];
  }, [currentSurvey?.properties]);

  // Set properties loading state based on survey data availability
  useEffect(() => {
    if (currentSurvey) {
      // Update map style if survey has a preferred marketMapStyle
      if (currentSurvey.marketMapStyle) {
        let currentStyle = currentSurvey.marketMapStyle;
        if (currentSurvey.marketMapStyle === "bingHybrid") {
          currentStyle = "emeraldBingHybrid";
        }
        if (currentSurvey.marketMapStyle === "esriHybrid") {
          currentStyle = "emeraldEsriHybrid";
        }
        const surveyStyle = MAP_STYLES.find(
          (style) => style.id === currentStyle,
        );
        if (surveyStyle && surveyStyle.id !== currentMapStyle.id) {
          // Directly set currentMapStyle to avoid dependency issues
          setCurrentMapStyle(surveyStyle);
        }
      }
    }
  }, [currentSurvey, currentMapStyle]);

  // Filtered lease comps for map display - excluding hidden map key categories
  const visibleLeaseComps = filterLeaseComps(leaseComps);
  const filteredLeaseComps = hiddenMapKeyColors.length
    ? visibleLeaseComps.filter((lc) => {
        const { iconColor } = getLeaseCompIconData(lc);
        return !hiddenMapKeyColors.includes(
          makeMapKeyCompositeKey(iconColor, false, "LeaseComp"),
        );
      })
    : visibleLeaseComps;

  // Filtered POIs for map display - excluding hidden map key categories
  const visiblePois = filterPois(pois);
  const filteredPois = hiddenMapKeyColors.length
    ? visiblePois.filter((poi) => {
        const { iconColor } = getPoiIconData(poi);
        // categoryId (extId) matches buildCompositeKey in MapKey; fall back to categoryName
        const categoryKey =
          poi.categoryId || poi.category?.categoryName || "POI";
        return !hiddenMapKeyColors.includes(
          makeMapKeyCompositeKey(iconColor, false, categoryKey),
        );
      })
    : visiblePois;

  // Filtered properties for sidebar - includes all properties that pass shortlist/hidden filters
  const sidebarProperties = useMemo(() => {
    return properties.filter((property) => {
      // Apply shortlist filter based on query param or Redux state
      if (filters.showShortlistOnly && !property.isClientShortlist) {
        return false;
      }

      // Hidden filter - always respect this unless overridden by query param logic
      if (!filters.showHidden && property.isHidden) {
        return false;
      }

      return true;
    });
  }, [properties, filters]);

  // Create a map of property IDs to shortlist indices when in shortlist mode
  const shortlistIndexMap = useMemo(() => {
    if (!filters.showShortlistOnly) return new Map();

    const map = new Map<string, number>();
    sidebarProperties.forEach((property, index) => {
      map.set(property.id, index + 1); // Ascending serial number starting from 1
    });
    return map;
  }, [sidebarProperties, filters.showShortlistOnly]);

  // Filtered properties based on current filters and visibility
  const visibleProperties = filterPropertiesForMap(sidebarProperties);
  const filteredProperties = hiddenMapKeyColors.length
    ? visibleProperties.filter((property) => {
        const { iconColor } = getIconData(property);
        return !hiddenMapKeyColors.includes(
          makeMapKeyCompositeKey(
            iconColor,
            property.isCurrentLocation ?? false,
            "Property",
          ),
        );
      })
    : visibleProperties;

  // Helper function to get user-friendly error messages
  const getMapErrorMessage = useCallback((errorMessage: string): string => {
    if (errorMessage.includes("access token")) {
      return "Invalid Mapbox access token. Please check your configuration.";
    }
    if (errorMessage.includes("Failed to fetch")) {
      return "Unable to connect to Mapbox services. Please check your internet connection.";
    }
    if (errorMessage.includes("401")) {
      return "Unauthorized access to Mapbox. Please verify your access token.";
    }
    return "Failed to load map. Please refresh the page.";
  }, []);

  const renderPolygons = useCallback(() => {
    if (!map.current?.isStyleLoaded() || polygonsRef.current.length === 0) return;

    if (polygonIdsRef.current.length > 0) {
      removeAllPolygons(map.current, polygonIdsRef.current);
    }

    polygonIdsRef.current = addPolygonsToMap(map.current, polygonsRef.current);
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    // Named function for handling market map generation and upload following TypeScript guidelines
    const handleMarketMapGeneration = async (): Promise<void> => {
      if (!surveyId) {
        console.error("Survey ID is required for market map generation");
        return;
      }

      try {
        // Generate market map and get response with blob and payloadMedia
        const response = await marketMapGeneration(surveyId);

        // Convert blob to File object with proper constructor parameters
        const fileName = `marketmap-${surveyId}-${Date.now()}.png`;
        const file = new File([response.blob], fileName, {
          type: "image/png",
          lastModified: Date.now(),
        });

        // Upload using RTK Query mutation with proper async/await
        const uploadResult = await uploadMarketMapFile({
          file: file, // blob converted to proper File object
          media: response.payloadMedia, // payloadMedia passed as media parameter
        }).unwrap();

        // Handle successful upload following TypeScript conventions
        if (!uploadResult.success) {
          throw new Error(uploadResult.message || "Upload failed");
        }
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error occurred";
        console.error("Market map generation/upload failed:", errorMessage);
      }
    };

    try {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: currentMapStyle.url,
        center: marketMapData?.mapCentroid || DEFAULT_MAP_CENTER,
        projection: "mercator",
        zoom: marketMapData?.mapZoomLevel || DEFAULT_MAP_ZOOM,
        attributionControl: false,
      });

      // Add navigation controls
      //  map.current.addControl(new mapboxgl.NavigationControl(), "top-right");

      // Map load event with proper async/await pattern
      map.current.on("load", () => {
        addMarkersToMap();
        if (polygonsRef.current.length > 0) {
          renderPolygons();
        }
        if (marketMapData?.mapReset) {
          centerMapUtil(
            map.current,
            filteredProperties,
            currentSurvey?.isIncludeMapKey ?? false,
            leaseComps,
            polygonsRef.current,
          );
        }
        // Use setTimeout with proper async handling following TypeScript guidelines
        setTimeout(() => {
          if (currentSurvey && surveyId) {
            // Call the async function properly without blocking
            handleMarketMapGeneration().catch((error) => {
              console.error("Failed to generate market map:", error);
            });
          }
        }, MARKET_MAP_GENERATION_DELAY);
      });

      // Error handling
      map.current.on("error", (e) => {
        // Filter out non-critical source layer errors that don't affect functionality
        const errorMessage = e.error?.message || "";
        const isSourceLayerError = errorMessage.includes("Source layer") && 
                                   errorMessage.includes("does not exist");
        
        // Only log critical errors, suppress non-critical layer warnings
        if (!isSourceLayerError) {
          console.error("Map error:", e);
        }
      });
    } catch (error) {
      console.error("Failed to initialize map:", error);
    }

    // Cleanup
    return () => {
      if (map.current) {
        if (polygonIdsRef.current.length > 0) {
          removeAllPolygons(map.current, polygonIdsRef.current);
        }
        map.current.remove();
        map.current = null;
      }
    };
  }, [
    currentMapStyle.url,
    currentSurvey,
    surveyId,
    uploadMarketMapFile,
    getMapErrorMessage,
  ]);

  useEffect(() => {
    const marketMapStyle = currentSurvey?.marketMapStyle;
    if (
      !bingTilesData ||
      !map.current ||
      (marketMapStyle !== "emeraldBingHybrid" &&
        marketMapStyle !== "bingHybrid")
    )
      return;
    map.current.setStyle(bingTilesData);
    map.current?.once("idle", () => {
      if (map.current) {
        deleteMarkers(map.current);
      }
      addMarkersToMap();
      if (map.current) {
        addMarinePortLayer(
          map.current,
          marinePorts || [],
          currentSurvey?.isMarinePorts ?? false,
        );
      }
      renderPolygons();
    });
  }, [bingTilesData, renderPolygons]);

  useEffect(() => {
    if (!marinePorts) return;
    fetchGeoJson();
  }, [marinePorts]);

  const fetchGeoJson = async () => {
    try {
      if (!map.current) return;

      const addLayer = () => {
        if (map.current) {
          addMarinePortLayer(
            map.current,
            marinePorts || [],
            currentSurvey?.isMarinePorts ?? false,
          );
        }
      };

      if (map.current.isStyleLoaded()) {
        addLayer();
      } else {
        map.current.once("idle", addLayer);
      }
    } catch (error) {
      console.error("Failed to fetch marine ports data", error);
    }
  };

  // Radius circle helper functions - now using utility modules
  const addRadiusCircle = useCallback(
    (
      property: Property,
      radiusData: {
        radius?: { value: string | number }[];
        radiusLength?: string;
        radiusColor?: string;
        radiusLabel?: boolean;
      },
      radiusIndex: number,
    ) => {
      if (!map.current) return;

      addRadiusCircleUtil(
        map.current,
        property,
        radiusData,
        radiusIndex,
        getPDFCoordinates,
        { radiusCirclesRef, radiusLabelMarkersRef },
      );
    },
    [],
  );

  // Helper function to get marker display content with shortlist index support
  const getMarkerDisplayContent = useCallback(
    (property: Property) => {
      // First check if property is a current location (star icon takes priority)
      const originalContent = getPropertyMarkerContent(property, properties);
      if (originalContent === "starIcon") {
        return "starIcon";
      }

      // If in shortlist mode and property has a shortlist index, use it
      const shortlistIndex = shortlistIndexMap.get(property.id);
      if (shortlistIndex) {
        return shortlistIndex.toString();
      }

      // Otherwise use the original content
      return originalContent;
    },
    [properties, shortlistIndexMap],
  );

  // Create a square marker for a lease comp with custom color and optional radius rings (no popup for PDF/print)
  const createLeaseCompMarker = useCallback(
    (leaseComp: LeaseComp, index: number) => {
      const coords = getLeaseCompCoordinatesWithMapData(leaseComp);
      if (!coords) return null;

      const iconData = getLeaseCompIconData(leaseComp);
      const markerElement = createLeaseCompMarkerElement(
        index,
        iconData.iconColor,
        iconData.iconSize,
      );
      if (
        coords == null ||
        coords[0] == null ||
        coords[1] == null ||
        isNaN(coords[0]) ||
        isNaN(coords[1])
      ) {
        return null;
      }
      const marker = new mapboxgl.Marker(markerElement).setLngLat(coords);

      // Add radius circles if radius information is available in mapData
      if (
        iconData.radius &&
        Array.isArray(iconData.radius) &&
        iconData.radius.length > 0
      ) {
        const pseudoProperty = {
          id: `lc_${leaseComp.leaseCompId}`,
          latitude: coords[1],
          longitude: coords[0],
        } as Property;

        iconData.radius.forEach(
          (_radiusItem: { value: string | number }, rIndex: number) => {
            addRadiusCircle(
              pseudoProperty,
              {
                radius: iconData.radius,
                radiusLength: iconData.radiusLength,
                radiusColor: iconData.radiusColor,
                radiusLabel: iconData.radiusLabel,
              },
              rIndex,
            );
          },
        );
      }

      return marker;
    },
    [],
  );

  // Create a circle POI marker for print/PDF - no popup or interactions needed
  const createPoiMarker = useCallback((poi: POI) => {
    const coords = getPoiCoordinates(poi);
    if (!coords) return null;
    if (isNaN(coords[0]) || isNaN(coords[1])) return null;

    const {
      iconColor,
      iconSize,
      iconName,
      pinType,
      logoUrl,
      logoWhiteBackground,
    } = getPoiIconData(poi);
    const markerElement = createPoiMarkerElement(
      iconColor,
      iconSize,
      iconName,
      pinType,
      logoUrl,
      logoWhiteBackground,
    );

    return new mapboxgl.Marker(markerElement).setLngLat(coords);
  }, []);

  // Create property marker for print/PDF - no popups or interactions needed
  const createPropertyMarker = useCallback((property: Property) => {
    const markerElement = document.createElement("div");
    markerElement.className = "property-marker";
    markerElement.id = `property-marker-${property.id}`;
    const bamData = getIconData(property);
    const iconColor = bamData.iconColor;
    const iconSize = bamData.iconSize;

    // Get and validate display content
    const displayContent = getMarkerDisplayContent(property);
    const innerDiv = createPropertyMarkerSvgElement(
      displayContent,
      iconColor,
      iconSize,
    );
    markerElement.appendChild(innerDiv);
    // Create marker without popup for print purposes
    const pdfCoords = getPDFCoordinates(property);
    if (
      pdfCoords == null ||
      pdfCoords[0] == null ||
      pdfCoords[1] == null ||
      isNaN(pdfCoords[0]) ||
      isNaN(pdfCoords[1])
    ) {
      return null;
    }
    const marker = new mapboxgl.Marker(markerElement).setLngLat(pdfCoords);

    // Add radius circles if radius information is available
    if (property.bamMapData && typeof property.bamMapData === "string") {
      try {
        if (bamData.radius && Array.isArray(bamData.radius)) {
          bamData.radius.forEach(
            (_radiusItem: { value: string }, index: number) => {
              const radiusData = {
                radius: bamData?.radius,
                radiusLength: bamData.radiusLength,
                radiusColor: bamData.radiusColor,
                radiusLabel: bamData.radiusLabel,
              };
              addRadiusCircle(property, radiusData, index);
            },
          );
        }
      } catch (error) {
        console.warn(
          "Failed to parse bamMapData for radius:",
          property.id,
          error,
        );
      }
    }

    return marker;
  }, []);

  // Helper function to add markers to the map for print purposes
  const addMarkersToMap = () => {
    if (!map.current) return;

    // Clear existing markers
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];

    // Clear existing radius circles
    const allCircleIds = Array.from(radiusCirclesRef.current);
    allCircleIds.forEach((circleId) => {
      if (map.current?.getLayer(circleId)) {
        map.current.removeLayer(circleId);
      }
      if (map.current?.getSource(circleId)) {
        map.current.removeSource(circleId);
      }
    });
    radiusCirclesRef.current.clear();

    // If no filtered properties AND no lease comps AND no POIs, return
    if (
      filteredProperties.length === 0 &&
      filteredLeaseComps.length === 0 &&
      filteredPois.length === 0
    ) {
      return;
    }

    // Add POI markers first (bottom z-order)
    poiMarkersRef.current.forEach((m) => m.remove());
    poiMarkersRef.current = [];
    filteredPois.forEach((poi) => {
      const marker = createPoiMarker(poi);
      if (marker) {
        marker.addTo(map.current!);
        poiMarkersRef.current.push(marker);
      }
    });

    // Add lease comp markers next so they render above POIs
    leaseCompMarkersRef.current.forEach((m) => m.remove());
    leaseCompMarkersRef.current = [];
    filteredLeaseComps.forEach((lc, index) => {
      const marker = createLeaseCompMarker(lc, index);
      if (marker) {
        marker.addTo(map.current!);
        leaseCompMarkersRef.current.push(marker);
      }
    });

    // Add property markers last so they appear above lease comp pins
    filteredProperties.forEach((property) => {
      const marker = createPropertyMarker(property);
      if (marker) {
        marker.addTo(map.current!);
        markersRef.current.push(marker);
      }
    });
  };

  useEffect(() => {
    if (!map.current) return;
    map.current?.once("idle", () => {
      addMarkersToMap();
      if (map.current) {
        addMarinePortLayer(
          map.current,
          marinePorts || [],
          currentSurvey?.isMarinePorts ?? false,
        );
      }
      renderPolygons();
    });
  }, [filteredProperties, filteredLeaseComps, filteredPois, renderPolygons]);

  // Center map on properties
  const centerMapWrapper = useCallback(() => {
    centerMapUtil(
      map.current,
      filteredProperties,
      currentSurvey?.isIncludeMapKey ?? false,
      [
        ...leaseComps,
        ...pois.map(
          (poi) =>
            ({
              leaseCompId: poi.poiId,
              leaseCompFields: {
                latitude: String(poi.mapData?.latitude ?? poi.latitude ?? ""),
                longitude: String(
                  poi.mapData?.longitude ?? poi.longitude ?? "",
                ),
              },
            }) as unknown as LeaseComp,
        ),
      ],
      polygons,
    );
  }, [filteredProperties, leaseComps, pois, polygons]);

  // Auto-center when filters change
  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        filteredProperties.length > 0 ||
        leaseComps.length > 0 ||
        pois.length > 0 ||
        polygons.some((polygon) => !polygon.isHidden)
      ) {
        centerMapWrapper();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [
    filteredProperties.length,
    leaseComps.length,
    pois.length,
    polygons,
    centerMapWrapper,
  ]);

  // Trigger polygon rendering when data arrives
  useEffect(() => {
    if (!isIncludePolygons || isPolygonError || isPolygonFetching || polygons.length === 0) return;
    if (map.current?.isStyleLoaded()) renderPolygons();
  }, [isPolygonError, isPolygonFetching, polygons.length, isIncludePolygons, renderPolygons]);

  // Remove polygons when isIncludePolygons becomes false
  useEffect(() => {
    if (!isIncludePolygons && map.current && polygonIdsRef.current.length > 0) {
      removeAllPolygons(map.current, polygonIdsRef.current);
      polygonIdsRef.current = [];
    }
  }, [isIncludePolygons]);

  // Re-render polygons when map style changes
  useEffect(() => {
    if (!map.current || polygonsRef.current.length === 0) return;

    if (map.current.isStyleLoaded()) {
      renderPolygons();
    } else {
      map.current.once("idle", renderPolygons);
    }
  }, [renderPolygons, currentMapStyle.id, polygons.length]);

  const renderMapPageWithLegends = (pageIndex: number) => {
    const {
      pageProperties,
      pageLeaseComps,
      numberedPropertiesBeforePage,
      startLeaseIndexFrom,
      hasPageContent,
    } = getPageData(pageIndex);

    const isFirstPage = pageIndex === 0;

    return (
      <SurveyPDFPage mode="PDF" key={pageIndex}>
        <div className="h-full w-full bg-white grid grid-rows-[auto_1fr_auto]">
          {/* Header Section */}
          {currentSurvey?.isIncludeHeader && <PDFHeader title="Market Map" />}

          {/* Main Content Section - Conditional Layout Based on Paper Size */}
          <div
            className={`absolute inset-0 bottom-12 ${
              currentSurvey?.isIncludeHeader ? "top-20" : "top-0"
            }`}
          >
            {currentSurvey?.pdfPaperSize === PDFPaperSize.Portrait ? (
              // Portrait Mode: PortraitMapPage measures legend height dynamically
              <PortraitMapPage
                pageIndex={pageIndex}
                mapContainerRef={isFirstPage ? mapContainer : undefined}
                showMapKey={currentSurvey?.isIncludeMapKey ?? false}
                showLegend={currentSurvey?.isIncludeLegend ?? false}
                hasPageContent={hasPageContent}
                pageProperties={pageProperties}
                startIndexFrom={numberedPropertiesBeforePage}
                pageLeaseComps={pageLeaseComps}
                startLeaseIndexFrom={startLeaseIndexFrom}
              />
            ) : (
              // Landscape Mode: Map on right, legends on left
              <div className="h-full flex">
                {currentSurvey?.isIncludeLegend && hasPageContent && (
                  <div className="w-80 flex-shrink-0 overflow-y-auto">
                    <MarketMapLegends
                      mode="PDF"
                      pageProperties={pageProperties}
                      startIndexFrom={numberedPropertiesBeforePage}
                      pageLeaseComps={pageLeaseComps}
                      startLeaseIndexFrom={startLeaseIndexFrom}
                    />
                  </div>
                )}
                <div className="flex-1 relative">
                  <div
                    className="market-map-container w-full h-full"
                    ref={isFirstPage ? mapContainer : undefined}
                    id={`marketmap-${pageIndex}`}
                  >
                    {currentSurvey?.isIncludeMapKey && <MapKey />}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer Section */}
          <div className="absolute bottom-0 left-0 right-0 h-12">
            <div className="flex py-2 px-2.5 gap-x-3 items-center bg-primary-lighter pdf-grid-footer h-full">
              <Footer />
            </div>
          </div>
        </div>
      </SurveyPDFPage>
    );
  };

  return (
    <section className="builder-content">
      <section className="builder-body">
        <div className="builder-sidebar bg-white border-r sticky border-gray-300"></div>

        <div className="builder-main overflow-y-auto">
          {currentSurvey && (
            <div className="flex flex-col items-center py-4 gap-y-4">
              {renderMapPageWithLegends(0)}
            </div>
          )}
        </div>
      </section>
    </section>
  );
};

export default InteractiveSurveyMarketMapRendered;
